import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import { StepActionsProps } from '@features/reports2/types';
import { Trans } from '@lingui/macro';
import { ArrowBack } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogActions, Skeleton, Typography, type BoxProps } from '@mui/material';
import { PropsWithChildren, ReactNode, Suspense, type FC } from 'react';

export const InnerPaper: FC<BoxProps> = (props) => {
  return (
    <Box
      {...props}
      sx={{
        borderRadius: 1,
        bgcolor: '#1E262F',
        p: 2,
        ...props.sx,
      }}
    />
  );
};

export const StepContentBox: FC<BoxProps & { actions: StepActionsProps }> = ({
  children,
  actions,
  ...props
}) => {
  return (
    <Box
      {...props}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '78dvh',
        px: 5,
        pt: 2,
        pb: 1,
        ...props.sx,
      }}
    >
      <Box
        sx={{
          overflowY: 'auto',
        }}
      >
        {children}
      </Box>
      <StepActions {...actions} />
    </Box>
  );
};

export const StepTitle: FC<{ title: ReactNode; description: ReactNode }> = ({
  title,
  description,
}) => {
  return (
    <Box mb={2}>
      <Typography variant="h6">{title}</Typography>
      <Typography>{description}</Typography>
    </Box>
  );
};

export const StepFallback: FC<PropsWithChildren> = ({ children }) => {
  return (
    <ApolloErrorBoundary>
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: 'min(80vh, 800px)',
              px: 5,
              py: 4,
            }}
          >
            <Skeleton variant="rounded" height="100%" width="100%" />
          </Box>
        }
      >
        {children}
      </Suspense>
    </ApolloErrorBoundary>
  );
};

export const StepActions: FC<StepActionsProps> = ({
  onClose,
  onNextStep,
  onPrevStep,
  onFinish,
  nextStepEnabled,
  prevStepEnabled,
  showOnFinish,
  onFinishEnabled,
  isEditing,
  submitLoading,
}) => {
  return (
    <DialogActions
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pt: 2,
        gap: 2,
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
        <Button
          disabled={!prevStepEnabled}
          startIcon={<ArrowBack />}
          color="info"
          onClick={onPrevStep}
        >
          <Trans>Atrás</Trans>
        </Button>
        <Button color="error" variant="contained" onClick={() => onClose()}>
          <Trans>Cancelar</Trans>
        </Button>
      </Box>
      {showOnFinish ? (
        <LoadingButton
          loading={submitLoading}
          disabled={!onFinishEnabled}
          variant="contained"
          onClick={onFinish}
          color="success"
        >
          {isEditing ? <Trans>Guardar</Trans> : <Trans>Generar reporte</Trans>}
        </LoadingButton>
      ) : (
        <Button disabled={!nextStepEnabled} variant="contained" onClick={onNextStep}>
          <Trans>Siguiente</Trans>
        </Button>
      )}
    </DialogActions>
  );
};
